import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import VerifiedIcon from '@mui/icons-material/Verified';
import UndoIcon from '@mui/icons-material/Undo';
import { FDataGrid, FGrid } from '../grid';
import { IDeterminantReport } from './determinantBudget.models';
import {
  certifyDeterminantReport,
  fetchDeterminantReportTable,
  generateDeterminantReport,
  revokeDeterminantReportCertification,
  validateDeterminantReport,
} from './determinantReport.apis';
import {
  findDeterminantReportIssues,
  resolveDeterminantReportIssue,
} from './determinantReportIssue.apis';

type Props = {
  determinantbudgetid: number;
  report: IDeterminantReport;
  loading?: boolean;
  canGenerate: boolean;
  readOnly?: boolean;
  onReload: () => Promise<void> | void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
};

type State = {
  selectedcode: string;
  tableRows: any[];
  issues: any[];
  loading: boolean;
  certifyOpen: boolean;
  revokeOpen: boolean;
  note: string;
};

export default class DeterminantReportPanel extends React.Component<Props, State> {
  state: State = {
    selectedcode: '',
    tableRows: [],
    issues: [],
    loading: false,
    certifyOpen: false,
    revokeOpen: false,
    note: '',
  };

  componentDidMount() {
    const first = this.defaultTableCode(this.props.report.tables || []);
    this.setState({ selectedcode: first }, () => {
      if (first) this.loadTable(first);
    });
    this.loadIssues();
  }

  componentDidUpdate(prev: Props) {
    if (prev.report.tables !== this.props.report.tables && !this.state.selectedcode) {
      const first = this.defaultTableCode(this.props.report.tables || []);
      if (first) this.setState({ selectedcode: first }, () => this.loadTable(first));
    }
  }

  private fail = (e: unknown) =>
    this.props.onError?.((e as any)?.message || String(e));

  private tableNumber = (table: any): number => {
    if (Number(table?.tablenumber || 0) > 0) return Number(table.tablenumber);
    const match = String(table?.tablecode || '').match(/(\d+)$/);
    return match ? Number(match[1]) : 0;
  };

  private tableTitle = (table: any): string =>
    String(table?.title || table?.tablename || table?.tablecode || 'Report table');

  private defaultTableCode = (tables: any[]): string => {
    const generated = tables.find((x) =>
      ['GENERATED', 'VALIDATED'].includes(String(x?.generationstate || '').toUpperCase())
      && Number(x?.rowcount || 0) > 0
    );
    return generated?.tablecode || tables?.[0]?.tablecode || '';
  };

  private normalizeRows = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(payload?.sd)) return payload.sd;
    return [];
  };

  private dynamicColumns = () => {
    const first = this.state.tableRows?.[0];
    if (!first || typeof first !== 'object') return [];
    return Object.keys(first).map((field) => ({
      field,
      headerName: field,
      minWidth: 140,
      flex: 1,
    }));
  };

  loadTable = async (tablecode: string) => {
    if (!tablecode) return;
    this.setState({ selectedcode: tablecode, loading: true });
    try {
      const payload = await fetchDeterminantReportTable(
        this.props.determinantbudgetid,
        tablecode,
      );
      this.setState({ tableRows: this.normalizeRows(payload) });
    } catch (e) {
      this.fail(e);
    } finally {
      this.setState({ loading: false });
    }
  };

  loadIssues = async () => {
    try {
      const issues = await findDeterminantReportIssues(
        this.props.determinantbudgetid,
        'OPEN',
      );
      this.setState({ issues: issues || [] });
    } catch (e) {
      this.fail(e);
    }
  };

  generate = async () => {
    this.setState({ loading: true });
    try {
      await generateDeterminantReport(this.props.determinantbudgetid);
      this.props.onSuccess?.('Determinant Report Tables 4–18 generated.');
      await this.props.onReload();
      if (this.state.selectedcode) await this.loadTable(this.state.selectedcode);
    } catch (e) {
      this.fail(e);
    } finally {
      this.setState({ loading: false });
    }
  };

  validate = async () => {
    this.setState({ loading: true });
    try {
      await validateDeterminantReport(this.props.determinantbudgetid);
      this.props.onSuccess?.('Determinant Report validation completed.');
      await this.props.onReload();
      await this.loadIssues();
    } catch (e) {
      this.fail(e);
    } finally {
      this.setState({ loading: false });
    }
  };

  certify = async () => {
    this.setState({ loading: true });
    try {
      await certifyDeterminantReport(
        this.props.determinantbudgetid,
        this.state.note.trim(),
      );
      this.setState({ certifyOpen: false, note: '' });
      this.props.onSuccess?.('Determinant Report Tables 4–18 certified.');
      await this.props.onReload();
    } catch (e) {
      this.fail(e);
    } finally {
      this.setState({ loading: false });
    }
  };

  revoke = async () => {
    if (!this.state.note.trim()) return;
    this.setState({ loading: true });
    try {
      await revokeDeterminantReportCertification(
        this.props.determinantbudgetid,
        this.state.note.trim(),
      );
      this.setState({ revokeOpen: false, note: '' });
      this.props.onSuccess?.('Determinant Report certification revoked.');
      await this.props.onReload();
    } catch (e) {
      this.fail(e);
    } finally {
      this.setState({ loading: false });
    }
  };

  resolveIssue = async (row: any) => {
    const resolution = window.prompt('Resolution note');
    if (!resolution?.trim()) return;
    this.setState({ loading: true });
    try {
      await resolveDeterminantReportIssue(row.recid ?? row.rid, resolution.trim());
      this.props.onSuccess?.('Report issue resolved.');
      await this.loadIssues();
      await this.props.onReload();
    } catch (e) {
      this.fail(e);
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const report = this.props.report;
    const busy = Boolean(this.props.loading || this.state.loading);
    const tables = report.tables || [];
    const selected = tables.find((x) => x.tablecode === this.state.selectedcode);
    const readOnly = Boolean(this.props.readOnly);

    return (
      <Stack spacing={2}>
        <Alert severity={report.certified ? 'success' : report.stale ? 'warning' : 'info'}>
          Mandatory report readiness: {report.mandatoryvalidationreadiness ?? report.completeness ?? 0}%
          {' — '} {report.mandatoryvalidatedcount ?? 0}/{report.mandatorycount ?? 0} mandatory tables validated.
          {report.certified ? ' The current report version is certified.' : ''}
          {report.financiallycomplete === false ? ' Capability limitations remain disclosed below.' : ''}
        </Alert>

        {readOnly && <Alert severity="info">
          This Determinant Budget is a completed historical workspace. Report data and certification are read-only.
        </Alert>}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<AutorenewIcon />}
            disabled={!this.props.canGenerate || busy}
            onClick={this.generate}
          >
            Generate / Regenerate Tables 4–18
          </Button>
          <Button
            variant="contained"
            startIcon={<FactCheckIcon />}
            disabled={busy || readOnly}
            onClick={this.validate}
          >
            Validate Report
          </Button>
          {!report.certified ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<VerifiedIcon />}
              disabled={busy || readOnly || !report.ready || (report.issuecount || 0) > 0}
              onClick={() => this.setState({ certifyOpen: true, note: '' })}
            >
              Certify Report
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<UndoIcon />}
              disabled={busy || readOnly}
              onClick={() => this.setState({ revokeOpen: true, note: '' })}
            >
              Revoke Certification
            </Button>
          )}
          {report.certified && <Chip size="small" color="success" label="CERTIFIED" />}
        </Stack>

        {tables.length > 0 ? (
          <TextField
            select
            size="small"
            label="Consolidated Report Table"
            value={this.state.selectedcode}
            onChange={(e) => this.loadTable(e.target.value)}
            sx={{ maxWidth: 460 }}
          >
            {tables.map((t) => (
              <MenuItem key={t.tablecode} value={t.tablecode}>
                Table {this.tableNumber(t)}: {this.tableTitle(t)} — {t.generationstate || 'PENDING'} / {t.capability || 'UNASSESSED'}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Alert severity="info">
            The current DB workbench does not yet publish the Tables 4–18 registry.
            DB-03 will make the generated table registry first-class; no synthetic TABLE_04 code is assumed here.
          </Alert>
        )}

        {this.state.selectedcode && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              {selected ? `Table ${this.tableNumber(selected)}: ${this.tableTitle(selected)}` : this.state.selectedcode}
            </Typography>
            <FDataGrid
              title=""
              rows={this.state.tableRows || []}
              columns={this.dynamicColumns() as any}
              getRowId={(r: any) => r.rid ?? r.recid ?? r.id ?? r.code ?? JSON.stringify(r)}
              height={480}
            />
          </Box>
        )}

        {(report.limitations || []).length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Governed capability limitations</Typography>
            <Stack spacing={0.75}>
              {(report.limitations || []).map((x: any) => (
                <Alert key={x.tablecode} severity={x.mandatory ? 'warning' : 'info'}>
                  Table {this.tableNumber(x)} — {this.tableTitle(x)}: {x.capability}
                  {x.validationmessage ? ` — ${x.validationmessage}` : ''}
                </Alert>
              ))}
            </Stack>
          </Paper>
        )}

        <FDataGrid
          title="Open Determinant Report Issues"
          rows={this.state.issues || []}
          columns={[
            { field: 'severity', headerName: 'Severity', width: 120 },
            { field: 'tablecode', headerName: 'Table', width: 110 },
            { field: 'rulecode', headerName: 'Rule', width: 150 },
            { field: 'message', headerName: 'Issue', flex: 1, minWidth: 300 },
            {
              field: 'resolve',
              headerName: '',
              width: 120,
              renderCell: (p: any) => (
                <Button size="small" disabled={readOnly} onClick={() => this.resolveIssue(p.row)}>
                  Resolve
                </Button>
              ),
            },
          ] as any}
          getRowId={(r: any) => r.recid ?? r.rid}
          height={320}
        />

        <Dialog open={this.state.certifyOpen} onClose={() => this.setState({ certifyOpen: false })} fullWidth maxWidth="sm">
          <DialogTitle>Certify Determinant Report</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Certification note"
              value={this.state.note}
              onChange={(e) => this.setState({ note: e.target.value })}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ certifyOpen: false })}>Cancel</Button>
            <Button variant="contained" disabled={busy} onClick={this.certify}>Certify</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={this.state.revokeOpen} onClose={() => this.setState({ revokeOpen: false })} fullWidth maxWidth="sm">
          <DialogTitle>Revoke Report Certification</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Revocation reason"
              value={this.state.note}
              onChange={(e) => this.setState({ note: e.target.value })}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ revokeOpen: false })}>Cancel</Button>
            <Button variant="contained" color="warning" disabled={busy || !this.state.note.trim()} onClick={this.revoke}>Revoke</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }
}
